import { useEffect, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Play, Pause, ArrowLeft, Music2, Instagram, Share2, Disc3, Download, Heart } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FollowButton from "../components/FollowButton";
import { toast } from "sonner";
import { useAudioPlayer, setRadioScope } from "../hooks/useAudioPlayer";
import { isLiked, toggleLike } from "../lib/userPrefs";
import { slugifyArtist } from "../lib/slug";

const API = process.env.REACT_APP_BACKEND_URL;

// Compact number formatter for mobile (1.2M, 153k, 5.3k)
function formatPlays(n) {
  const num = Number(n) || 0;
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(num >= 10_000_000 ? 0 : 1).replace(".", ",") + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(num >= 10_000 ? 0 : 1).replace(".", ",") + "k";
  return num.toLocaleString("pt-BR");
}

export default function CDDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [cd, setCd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const { playingId, handlePlay } = useAudioPlayer();
  const autoplayedRef = useRef(false);

  useEffect(() => {
    fetch(`${API}/api/cd-submissions/${id}`)
      .then(async r => { if (!r.ok) throw new Error("not found"); return r.json(); })
      .then((data) => {
        setCd(data);
        setLiked(isLiked("cds", data.id));
      })
      .catch(() => setCd(null))
      .finally(() => setLoading(false));
  }, [id]);

  const handleLike = () => {
    if (!cd) return;
    const now = toggleLike("cds", cd.id);
    setLiked(now);
    toast.success(now ? "Adicionado aos favoritos" : "Removido dos favoritos");
  };

  // Convert CD tracks into music-like objects for the global player.
  // Use a stable per-track id and support BOTH preview_url and legacy audio_url.
  const buildQueue = () =>
    (cd?.tracks || [])
      .map((t, idx) => {
        const src = t.preview_url || t.audio_url || "";
        return {
          id: `cd-${cd.id}-${idx}`,
          title: t.title,
          artist_name: cd.artist_name,
          artist_image: cd.cover_url ? `${API}${cd.cover_url}` : null,
          preview_url: src && !src.startsWith("http") ? `${API}${src}` : src,
          _source_cd_id: cd.id,
        };
      })
      .filter((t) => t.preview_url);

  const togglePlay = (idx) => {
    const queue = buildQueue();
    const target = queue[idx];
    if (!target) return;
    // "Escopo do artista": ao terminar o álbum, o player carrega OUTRO CD DO MESMO ARTISTA.
    // Só transiciona para a rádio geral quando o catálogo do artista se esgotar.
    setRadioScope({ type: "artist", artistName: cd.artist_name });
    handlePlay(target, queue);
  };

  // Autoplay quando o CD chega com ?autoplay=1 (vindo do "OUÇA AGORA" do hero)
  useEffect(() => {
    if (!cd || autoplayedRef.current) return;
    if (searchParams.get("autoplay") !== "1") return;
    const queue = buildQueue();
    if (queue.length === 0) return;
    autoplayedRef.current = true;
    // Pequeno delay pra garantir que o player global já está montado
    const t = setTimeout(() => {
      setRadioScope({ type: "artist", artistName: cd.artist_name });
      handlePlay(queue[0], queue);
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cd, searchParams]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: `${cd.artist_name} - ${cd.cd_title}`, url });
        return;
      } catch (err) {
        // User cancelled native share, fall through to clipboard
        console.debug("Native share cancelled", err);
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado!");
    } catch (err) {
      console.error("Clipboard write failed", err);
      toast.error("Não foi possível copiar o link");
    }
  };

  const downloadTrack = (e, idx, title) => {
    e.stopPropagation();
    const url = `${API}/api/cd-submissions/${id}/track/${idx}/download`;
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success(`Baixando "${title}"...`);
  };

  const [zipping, setZipping] = useState(false);
  const downloadZip = () => {
    if (zipping) return;
    setZipping(true);
    toast.info("Preparando ZIP do CD inteiro...");
    const url = `${API}/api/cd-submissions/${id}/download`;
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Re-enable button after a short delay (browser handles real download in background)
    setTimeout(() => setZipping(false), 3500);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Header />
        <div className="flex justify-center py-32">
          <div className="w-12 h-12 border-4 border-[#FF007F] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!cd) {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-32 text-center">
          <Disc3 className="w-20 h-20 text-zinc-700 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-4">CD não encontrado</h1>
          <Link to="/vitrine" className="text-[#FF007F] hover:underline">Voltar para a Vitrine</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />

      <main className="pt-24">
        {/* Hero with cover blur */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img src={`${API}${cd.cover_url}`} alt="" className="w-full h-full object-cover blur-3xl opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/80 to-[#050505]" />
          </div>

          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-10">
            <Link to="/vitrine" className="inline-flex items-center gap-2 text-zinc-400 hover:text-[#FF007F] transition-colors mb-6 text-sm">
              <ArrowLeft size={16} /> Voltar à vitrine
            </Link>

            <div className="grid md:grid-cols-[280px_1fr] gap-8 items-end">
              <img src={`${API}${cd.cover_url}`} alt={cd.cd_title}
                className="w-full max-w-[280px] aspect-square object-cover rounded-2xl shadow-2xl shadow-black/50" />
              <div>
                <p className="text-xs text-[#FF007F] uppercase tracking-[0.2em] font-bold mb-2">CD INDEPENDENTE</p>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] mb-3">{cd.cd_title}</h1>
                <p className="text-2xl text-zinc-300 mb-4">
                  <Link to={`/artista/${slugifyArtist(cd.artist_name)}`} className="hover:text-[#FF007F] transition-colors">
                    {cd.artist_name}
                  </Link>
                </p>
                <div className="mb-4">
                  <FollowButton artistName={cd.artist_name} slug={slugifyArtist(cd.artist_name)} size="md" />
                </div>
                {cd.description && <p className="text-zinc-500 max-w-2xl mb-5">{cd.description}</p>}
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-sm flex items-center gap-1.5">
                    <Music2 size={14}/> {cd.tracks?.length || 0} faixas
                  </span>
                  {(() => {
                    const totalPlays = (cd.tracks || []).reduce((acc, tr) => acc + (Number(tr.plays) || 0), 0);
                    if (totalPlays <= 0) return null;
                    return (
                      <span
                        className="px-3 py-1.5 bg-[#FF007F]/10 border border-[#FF007F]/30 rounded-full text-sm flex items-center gap-1.5 text-[#FF007F] font-semibold"
                        title={`${totalPlays.toLocaleString("pt-BR")} reproduções`}
                        data-testid="cd-total-plays"
                      >
                        <Play size={12} fill="currentColor" /> {formatPlays(totalPlays)} plays
                      </span>
                    );
                  })()}
                  <button onClick={handleLike}
                    className={`px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 font-semibold transition-all border ${
                      liked
                        ? "bg-[#FF007F]/20 text-[#FF007F] border-[#FF007F]/60"
                        : "bg-white/5 text-zinc-300 hover:text-[#FF007F] hover:border-[#FF007F]/40 border-white/10"
                    }`}
                    data-testid="like-cd-btn">
                    <Heart size={14} fill={liked ? "#FF007F" : "none"}/> {liked ? "Curtido" : "Curtir"}
                  </button>
                  {cd.instagram && (
                    <a href={`https://instagram.com/${cd.instagram.replace(/^@/, "")}`} target="_blank" rel="noopener noreferrer"
                      className="px-3 py-1.5 bg-white/5 hover:bg-pink-500/20 border border-white/10 hover:border-pink-500/40 rounded-full text-sm flex items-center gap-1.5 transition-all">
                      <Instagram size={14}/> {cd.instagram}
                    </a>
                  )}
                  <button onClick={share}
                    className="px-3 py-1.5 bg-[#FF007F]/15 hover:bg-[#FF007F]/30 border border-[#FF007F]/40 rounded-full text-sm flex items-center gap-1.5 text-[#FF007F] font-semibold transition-all"
                    data-testid="share-cd-btn">
                    <Share2 size={14}/> Compartilhar
                  </button>
                  <button onClick={downloadZip}
                    disabled={zipping || !cd.tracks?.length}
                    className="px-4 py-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 disabled:cursor-wait rounded-full text-sm flex items-center gap-1.5 text-black font-bold transition-all shadow-lg shadow-emerald-500/20"
                    data-testid="download-cd-zip-btn"
                    title="Baixa todas as faixas + capa em um único arquivo .zip">
                    <Download size={14}/> {zipping ? "Preparando..." : "Baixar CD"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tracks */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <h2 className="font-heading text-2xl font-bold mb-5 flex items-center gap-2">
            <Music2 size={20} className="text-[#FF007F]"/> Faixas
          </h2>
          <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl overflow-hidden divide-y divide-white/5">
            {(cd.tracks || []).map((t, idx) => {
              const trackId = `cd-${cd.id}-${idx}`;
              const isPlaying = playingId === trackId;
              return (
              <div key={trackId}
                className="w-full flex items-center gap-4 p-4 hover:bg-white/5 transition-colors group"
                data-testid={`track-row-${idx}`}>
                <button onClick={() => togglePlay(idx)}
                  className="flex items-center gap-4 flex-1 text-left min-w-0"
                  data-testid={`track-play-${idx}`}>
                  <span className="w-8 text-center font-bold text-zinc-600 group-hover:text-[#FF007F] tabular-nums">{String(t.track_number || idx + 1).padStart(2, "0")}</span>
                  <div className="w-10 h-10 rounded-full bg-[#FF007F]/15 flex items-center justify-center group-hover:bg-[#FF007F] transition-colors flex-shrink-0">
                    {isPlaying ? <Pause size={16}/> : <Play size={16} className="ml-0.5"/>}
                  </div>
                  <span className="flex-1 font-medium truncate">{t.title}</span>
                  {isPlaying && (
                    <span className="flex items-center gap-0.5 mr-2">
                      <span className="w-1 h-3 bg-[#FF007F] animate-pulse rounded-full"></span>
                      <span className="w-1 h-4 bg-[#FF007F] animate-pulse rounded-full" style={{ animationDelay: "0.15s" }}></span>
                      <span className="w-1 h-2 bg-[#FF007F] animate-pulse rounded-full" style={{ animationDelay: "0.3s" }}></span>
                    </span>
                  )}
                  <span
                    className="flex items-center gap-1 text-xs sm:text-sm text-zinc-400 tabular-nums shrink-0 pr-2 sm:pr-3"
                    title={`${(Number(t.plays) || 0).toLocaleString("pt-BR")} plays`}
                    data-testid={`track-plays-${idx}`}
                  >
                    <Play size={10} className="text-[#FF007F]/70" fill="currentColor" />
                    <span className="hidden sm:inline">{(Number(t.plays) || 0).toLocaleString("pt-BR")}</span>
                    <span className="sm:hidden">{formatPlays(t.plays)}</span>
                  </span>
                </button>
                <button onClick={(e) => downloadTrack(e, idx, t.title)}
                  className="p-2 rounded-full text-zinc-400 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all flex-shrink-0"
                  data-testid={`download-track-${idx}`}
                  title="Baixar essa faixa">
                  <Download size={18}/>
                </button>
              </div>
            );})}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
